import React, { useState, useEffect, useCallback } from 'react';
import ChatInput from './ChatInput';
import QnACard from './QnACard';
import { AVAILABLE_AI_MODELS, ALL_AI_MODELS, MODEL_DISPLAY_NAMES } from '../config/chatConfig';
import { useChatInput } from '../context/ChatInputContext';
import { detectMobile, detectTablet, detectMobileOrTablet } from '../utils/layoutUtils';

/**
 * Fixed-position chat input that consumes props from Chatty via context.
 * sidebarOpen is passed directly from App.js for immediate updates.
 */
const ChatInputFixed = ({ sidebarOpen = false }) => {
  const { chatInputProps, qnaData, setQnaData } = useChatInput();
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Detect mobile/tablet - check immediately for SSR
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return detectMobile();
    }
    return false;
  });
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      return detectTablet();
    }
    return false;
  });
  // eslint-disable-next-line no-unused-vars
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      return detectMobileOrTablet();
    }
    return false;
  });

  // Update on resize
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(detectMobile());
      setIsTablet(detectTablet());
      setIsMobileOrTablet(detectMobileOrTablet());
    };
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Scroll detection on document body (the actual scrolling element)
  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.body;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollDown(!isNearBottom);
    };

    document.body.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => {
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!chatInputProps) return null;
  
  const {
    selectedModel,
    setSelectedModel,
    handleSendMessage,
    isLoading,
    isMimicking,
    isCreatingSession,
    handleStopMessage,
    selectedImages,
    setSelectedImages,
    setUploadImages,
    handleImageUpload,
    user,
    isPopupEnabled,
    setIsPopupOpen,
    supportsImages,
    isLimitReached,
    scrollToBottom,
    setMessages,
    isCentered = false,
    currentSessionId,
    navigate,
    refreshSessions,
  } = chatInputProps;

  // Device-specific styles
  const getDeviceStyle = () => {
    // Mobile phone - full width edge-to-edge
    if (isMobile) {
      return {
        left: 0,
        right: 0,
        width: '100vw',
        maxWidth: '100vw',
        transform: 'none',
        padding: 0,
      };
    }
    // Tablet - narrower centered box
    if (isTablet) {
      return {
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        maxWidth: '600px',
        padding: '0 16px',
      };
    }
    // Desktop - default positioning from CSS
    return {};
  };
  const deviceStyle = getDeviceStyle();

  const centeredStyle = isCentered ? {
    top: '42%',
    bottom: 'auto',
    transform: isMobile
      ? 'translateY(-50%)'
      : isTablet
        ? 'translateX(-50%) translateY(-50%)'
        : `translateY(-50%) ${sidebarOpen ? 'translateX(140px)' : 'translateX(0)'}`,
  } : {};

  // Dynamic greeting based on time of day (same as Chatty.js)
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = user?.name?.split(' ')[0] || 'there';

    if (hour >= 23 || hour < 3) {
      return `It's late, ${firstName}. What's up?`;
    } else if (hour >= 3 && hour < 12) {
      return `Good morning, ${firstName}`;
    } else if (hour >= 12 && hour < 18) {
      return `Good afternoon, ${firstName}`;
    } else {
      return `Good evening, ${firstName}`;
    }
  };

  return (
    <div
      id="chat-input-fixed"
      className={`fixed w-full ${isMobile || isTablet ? '' : 'max-w-[800px]'} z-50 chatty-debug-input ${sidebarOpen ? 'sidebar-open' : ''} ${isCentered ? 'is-centered' : ''} ${isTablet ? 'is-tablet' : ''}`}
      style={{ ...centeredStyle, ...deviceStyle }}
    >
      <div className="relative flex flex-col w-full">
        {/* NEW Welcome greeting - above chat input, only when centered */}
        {isCentered && (
          <h1
            className="text-4xl md:text-5xl font-light text-center px-4 mb-0"
            style={{ color: 'var(--text-primary)' }}
          >
            {getGreeting()}
          </h1>
        )}
        <div className="-mt-2 mb-0 flex items-center justify-center relative">
          
          {/* Scroll to bottom button - only show when not centered and has scroll */}
          <div className={`transition-all duration-500 ${showScrollDown && !isCentered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
            <button
              onClick={scrollToBottom}
              className="relative rounded-full p-2.5 shadow-lg transition-all duration-200 hover:scale-105"
              style={{
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                color: 'var(--text-primary)'
              }}
              aria-label="Scroll to bottom"
            >
              {/* Glass blur background */}
              <div className="absolute inset-0 rounded-full chatty-glass-blur" />
              {/* Icon content */}
              <div className="relative z-10">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-3 w-3" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
        {qnaData ? (
          <div className={`pb-0 pt-2 -mb-0.5 ${isCentered ? 'mx-6' : 'mx-0'} sm:mx-2.5`}>
            <div className="w-full max-w-full sm:max-w-[800px] mx-auto">
              <QnACard
                questions={qnaData.questions}
                onSubmit={(formatted) => {
                  setQnaData(null);
                  handleSendMessage(formatted);
                }}
                onDismiss={(dismissText) => {
                  setQnaData(null);
                  handleSendMessage(dismissText);
                }}
              />
            </div>
          </div>
        ) : (
          <ChatInput
            onSend={handleSendMessage}
            isLoading={isLoading}
            isMimicking={isMimicking}
            isCreatingSession={isCreatingSession}
            onStop={handleStopMessage}
            collapseSidebar={() => {}}
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            setUploadImages={setUploadImages}
            handleImageUpload={handleImageUpload}
            user={user}
            isPopupEnabled={isPopupEnabled}
            setIsPopupOpen={setIsPopupOpen}
            supportsImages={supportsImages}
            isLimitReached={isLimitReached}
            isCentered={isCentered}
            isMobile={isMobile}
            isTablet={isTablet}
            currentSessionId={currentSessionId}
            navigate={navigate}
            refreshSessions={refreshSessions}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            onAddMessage={(message) => {
              // Add voice transcript messages directly to chat UI
              if (message && message.text && setMessages) {
                setMessages(prevMessages => [...prevMessages, message]);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ChatInputFixed;